import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { Tooltip } from '../Tooltip';
import styles from './Typography.module.scss';
import { applyForwardedRef, makeOverflowWatcher } from './Typography.utils';
const getWeightClassName = (weight, fontFamily)=>{
    if (fontFamily === 'hero') {
        return styles["hero".concat(weight ? weight.charAt(0).toUpperCase() + weight.slice(1) : 'Light', "Weight")];
    }
    return styles["".concat(weight, "Weight")];
};
const MARKDOWN_BOLD_PATTERN = /\*\*(.+?)\*\*/;
const renderMarkdownBold = (text, boldClassName, testId)=>text.split(MARKDOWN_BOLD_PATTERN).map((part, index)=>index % 2 === 1 ? /*#__PURE__*/ _jsx("strong", _object_spread_props(_object_spread({
            className: boldClassName
        }, testId != null ? {
            'data-testid': "".concat(testId, "-bold-").concat((index - 1) / 2)
        } : {}), {
            children: part
        }), index) : part);
/** Renders text with configurable presets. */ const BaseTypography = /*#__PURE__*/ React.forwardRef((props, ref)=>{
    const { className, variant, weight, fontFamily, skin, capitalize, as, children, style, textDecoration, align, noWrap, breakWords, showWhiteSpaces, applyMarkdownBold, loading, testId } = props, rest = _object_without_properties(props, [
        "className",
        "variant",
        "weight",
        "fontFamily",
        "skin",
        "capitalize",
        "as",
        "children",
        "style",
        "textDecoration",
        "align",
        "noWrap",
        "breakWords",
        "showWhiteSpaces",
        "applyMarkdownBold",
        "loading",
        "testId"
    ]);
    const Component = as || 'div';
    const content = applyMarkdownBold && typeof children === 'string' ? renderMarkdownBold(children, getWeightClassName('normal', fontFamily), testId) : children;
    const renderedStyle = useMemo(()=>style ? _object_spread_props(_object_spread({}, style), {
            textDecoration
        }) : {
            textDecoration
        }, [
        style,
        textDecoration
    ]);
    return /*#__PURE__*/ _jsx(Component, _object_spread_props(_object_spread({
        style: renderedStyle,
        ref: ref,
        className: cn(styles.root, styles["".concat(variant, "Variant")], getWeightClassName(weight, fontFamily), skin !== 'inherit' && styles["".concat(skin, "Skin")], align && styles["".concat(align, "Align")], {
            [styles.capitalize]: capitalize,
            [styles.noWrap]: noWrap,
            [styles.breakWords]: breakWords,
            [styles.showWhiteSpaces]: showWhiteSpaces,
            [styles.shimmer]: loading
        }, className)
    }, rest, testId != null ? {
        'data-testid': testId
    } : {}), {
        children: content
    }));
});
BaseTypography.defaultProps = {
    variant: 'paragraph1',
    skin: 'standard',
    weight: 'light'
};
BaseTypography.displayName = 'BaseTypography';
/**
 * Renders a Typography component, limited to one-line of text, with an ellipsis
 * if the text is overflowing; wrapped in a tooltip. Will show the full text in the
 * tooltip only if text is overflowing.
 */ export const Typography = /*#__PURE__*/ React.forwardRef(function Typography(props, ref) {
    const { className, children, ellipsis, tooltipProps, variant = 'paragraph1', skin = 'standard', weight = 'light', fontFamily = 'default', hideTooltip, tooltipTestId, onChange, onFocus, onBlur } = props, rest = _object_without_properties(props, [
        "className",
        "children",
        "ellipsis",
        "tooltipProps",
        "variant",
        "skin",
        "weight",
        "fontFamily",
        "hideTooltip",
        "tooltipTestId",
        "onChange",
        "onFocus",
        "onBlur"
    ]);
    const textRef = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const overflowWatcher = useMemo(()=>makeOverflowWatcher(setIsOverflowing), []);
    const attachEllipsisRef = useCallback((el)=>{
        textRef.current = el;
        overflowWatcher.watch(el);
        applyForwardedRef(ref, el);
    }, [
        ref,
        overflowWatcher
    ]);
    useEffect(()=>()=>overflowWatcher.disconnect(), [
        overflowWatcher
    ]);
    let title = '';
    if (!isFocused && isOverflowing && textRef.current) {
        if (tooltipProps && tooltipProps.title) {
            title = tooltipProps.title;
        } else if (typeof children === 'string' && !props.applyMarkdownBold) {
            title = children;
        } else if (textRef.current.textContent) {
            title = textRef.current.textContent;
        } else if (textRef.current.value) {
            title = textRef.current.value;
        }
    }
    return ellipsis && !hideTooltip ? /*#__PURE__*/ _jsx(Tooltip, _object_spread_props(_object_spread({
        placement: "top"
    }, tooltipProps), {
        title: tooltipTestId && title ? /*#__PURE__*/ _jsx("div", {
            "data-testid": tooltipTestId,
            children: title
        }) : title,
        children: /*#__PURE__*/ _jsx(BaseTypography, _object_spread_props(_object_spread({}, rest), {
            variant: variant,
            skin: skin,
            weight: weight,
            fontFamily: fontFamily,
            ref: attachEllipsisRef,
            onChange: (e)=>{
                // This is important for usage with as="input".
                setIsOverflowing(e.currentTarget.clientWidth < e.currentTarget.scrollWidth);
                onChange && onChange(e);
            },
            onFocus: (e)=>{
                // This is important for usage with as="input" to hide tooltip when focused.
                setIsFocused(true);
                onFocus && onFocus(e);
            },
            onBlur: (e)=>{
                // This is important for usage with as="input" to hide tooltip when focused.
                setIsFocused(false);
                onBlur && onBlur(e);
            },
            className: cn(styles.ellipsis, className),
            children: children
        }))
    })) : /*#__PURE__*/ _jsx(BaseTypography, _object_spread_props(_object_spread({}, rest), {
        variant: variant,
        skin: skin,
        weight: weight,
        fontFamily: fontFamily,
        ref: ref,
        className: cn(className, {
            [styles.ellipsis]: ellipsis
        }),
        onChange: onChange,
        onFocus: onFocus,
        onBlur: onBlur,
        children: children
    }));
});
